import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { MenuButton } from '../../MenuButton';
/**
 * Renders toolbar actions based on screen size and available space.
 * Important actions are shown directly, others are collapsed under a "More" dropdown.
 *
 * @param {ToolbarActionsProps} root0 - Props for ToolbarActions.
 * @param {ToolbarAction[]} root0.actions - Array of toolbar action objects.
 * @param {boolean} [root0.filterPanelVisible] - Indicates if the filter panel is currently visible.
 * @param {boolean} [root0.searchVisible] - Indicates if search input is visible.
 *
 * @returns {JSX.Element} The rendered toolbar actions component.
 */
export const ToolbarActions = ({ actions, filterPanelVisible, searchVisible }) => {
    const theme = useTheme();
    const downSmScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const downLgScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const downXlScreen = useMediaQuery(theme.breakpoints.down('xl'));
    let importantActions = [];
    let actionsMore = [];
    if (downSmScreen) {
        return React.createElement(Actions, { actions: importantActions, actionsMore: actions });
    }
    if (downLgScreen) {
        const buttonsLength = filterPanelVisible || searchVisible ? 0 : 1;
        if (actions.length > buttonsLength) {
            importantActions = actions.slice(0, buttonsLength);
            actionsMore = actions.slice(buttonsLength, actions.length);
        }
        else {
            importantActions = actions;
        }
        return React.createElement(Actions, { actions: importantActions, actionsMore: actionsMore });
    }
    const buttonsLength = (filterPanelVisible || searchVisible) && downXlScreen ? 1 : 4;
    if (actions.length > buttonsLength) {
        importantActions = actions.slice(0, buttonsLength);
        actionsMore = actions.slice(buttonsLength, actions.length);
    }
    else {
        importantActions = actions;
    }
    return React.createElement(Actions, { actions: importantActions, actionsMore: actionsMore });
};
const Actions = ({ actions, actionsMore }) => {
    const moreMenuAction = {
        label: 'More',
        actionProps: {
            startIcon: React.createElement(MoreHoriz, null),
        },
        'data-test': 'toolbar-more-button',
        actions: flatten(actionsMore),
    };
    return (React.createElement(React.Fragment, null,
        !!actions && actions.map((action) => React.createElement(ActionItem, { key: action.label, action: action })),
        !!actionsMore.length && React.createElement(ActionItem, { action: moreMenuAction })));
};
const ActionItem = ({ action }) => {
    var _a, _b, _c;
    if ((_a = action.actions) === null || _a === void 0 ? void 0 : _a.length) {
        return (React.createElement(Box, { sx: { mr: 2 } },
            React.createElement(MenuButton, { color: 'primary', disabled: (_b = action.actionProps) === null || _b === void 0 ? void 0 : _b.disabled, menuItems: action.actions.map((button) => {
                    return Object.assign(Object.assign(Object.assign({}, button === null || button === void 0 ? void 0 : button.actionProps), button), { onClick: () => ((button === null || button === void 0 ? void 0 : button.onClick) ? button.onClick() : undefined) });
                }), "data-test": action['data-test'], startIcon: (_c = action.actionProps) === null || _c === void 0 ? void 0 : _c.startIcon, variant: 'outlined' }, action.label)));
    }
    return (React.createElement(Button, Object.assign({ sx: { mr: 2 }, variant: 'outlined', color: 'primary', onClick: () => ((action === null || action === void 0 ? void 0 : action.onClick) ? action.onClick() : undefined), "data-test": action['data-test'] }, action === null || action === void 0 ? void 0 : action.actionProps), action.label));
};
function flatten(actions) {
    const flatActions = [];
    actions.forEach((action) => {
        if (action === null || action === void 0 ? void 0 : action.actions) {
            flatActions.push(...flatten(action.actions));
        }
        else {
            flatActions.push(action);
        }
    });
    return flatActions;
}
